import { format } from 'date-fns';
import { dbDayDateToken } from '../../../static/dbDayDateToken';
import { InviteeStatusEnum } from '../../models/jobInviteeStatusEnum';
import { JobStatusEnum } from '../../models/jobStatusEnum';
import { PoolerJobsTabIdEnum } from '../../models/PoolerJobsTabId.enum';
import { RoleEnum } from '../../models/role.enum';
import { TransferStatusEnum } from '../../models/transferStatusEnum.enum';
import { User } from '../../models/user';
import { getUtcStartOfDay } from '../removeTimeZoneOffset';
import {
  getDispoActionRequiredBookableJobQuery,
  getDispoActionRequiredNoOpenInviteesJobQuery,
} from './getDispoActionRequiredJobQuery';
import { getDispoArchivedTabJobsQuery } from './getDispoArchivedTabJobsQuery';
import { getDispoBookedTabJobsQuery } from './getDispoBookedTabJobsQuery';
import { getDispoUnapprovedHoursQuery } from './getDispoUnapprovedHoursQuery';
import { getPoolerAnsweredTabJobsQuery } from './getPoolerAnsweredTabJobsQuery';
import { getPoolerArchivedTabJobsQuery } from './getPoolerArchivedTabJobsQuery';
import { getPoolerBookedTabJobsQuery } from './getPoolerBookedJobsQuery';
import { JobStateMongoQueryOptions } from './JobsMongoQueryOptions';

export function getJobStateMongoQuery(
  state: string,
  user: User,
  options: JobStateMongoQueryOptions = {},
): object[] {
  const {
    showCanceledWithArchived = false,
    showOnlyCanceledJobs = false,
    showOnlyActionRequiredBookableJobs = false,
    showOnlyActionRequiredNoOpenInviteesJobs = false,
    showOnlyBookedJobs = false,
    showOnlyUnapprovedHours = false,
  } = options;

  const normalizedState = state.toUpperCase();
  const isPooler = RoleEnum.POOL === user.role;
  const conditions: object[] = [];

  switch (normalizedState) {
    case JobStatusEnum.CLOSED:
      if (isPooler) {
        conditions.push(getPoolerBookedTabJobsQuery(user));
      } else if (user.role === RoleEnum.DISPO) {
        conditions.push(getDispoBookedTabJobsQuery(user));
      }
      break;
    case JobStatusEnum.ARCHIVED:
      if (isPooler) {
        conditions.push(getPoolerArchivedTabJobsQuery(user));
      } else if (user.role === RoleEnum.DISPO) {
        conditions.push(
          ...getDispoArchivedTabJobsQuery(user, {
            showCanceledWithArchived,
            showOnlyCanceledJobs,
            showOnlyBookedJobs,
          }),
        );
        if (showOnlyUnapprovedHours) {
          conditions.push(getDispoUnapprovedHoursQuery(user));
        }
      }
      break;
    case JobStatusEnum.OPEN:
      conditions.push(
        ...getOpenStateQuery(user, isPooler, {
          showOnlyActionRequiredBookableJobs,
          showOnlyActionRequiredNoOpenInviteesJobs,
        }),
      );
      break;
    case PoolerJobsTabIdEnum.ANSWERED:
      if (!isPooler) {
        throw new Error("tabId 'answered' is not available for dispo");
      }
      conditions.push(getPoolerAnsweredTabJobsQuery(user));
      break;
    default:
      console.error('TabId' + normalizedState + ' not handled in mongoQuery');
      break;
  }

  return conditions;
}

function getOpenStateQuery(
  user: User,
  isPooler: boolean,
  options: Pick<
    JobStateMongoQueryOptions,
    | 'showOnlyActionRequiredBookableJobs'
    | 'showOnlyActionRequiredNoOpenInviteesJobs'
  >,
): object[] {
  const conditions: object[] = [];
  const {
    showOnlyActionRequiredBookableJobs,
    showOnlyActionRequiredNoOpenInviteesJobs,
  } = options;

  if (user.role === RoleEnum.DISPO) {
    conditions.push({
      status: JobStatusEnum.OPEN,
      $or: [
        {
          transfers: {
            $elemMatch: {
              'targetCustomer.id': user.customerId,
              status: { $ne: TransferStatusEnum.ARCHIVED },
              declinedAt: { $exists: false },
            },
          },
        },
        {
          transfers: {
            $not: {
              $elemMatch: {
                'targetCustomer.id': user.customerId,
                transferredAt: { $exists: true },
              },
            },
          },
        },
      ],
    });

    if (
      showOnlyActionRequiredBookableJobs ||
      showOnlyActionRequiredNoOpenInviteesJobs
    ) {
      const actionRequiredConditions: object[] = [];
      if (showOnlyActionRequiredBookableJobs) {
        actionRequiredConditions.push(
          getDispoActionRequiredBookableJobQuery(user.customerId),
        );
      }
      if (showOnlyActionRequiredNoOpenInviteesJobs) {
        actionRequiredConditions.push(
          getDispoActionRequiredNoOpenInviteesJobQuery(user.customerId),
        );
      }
      conditions.push({ $or: actionRequiredConditions });
    }
  }

  if (isPooler) {
    conditions.push({
      status: {
        $in: [JobStatusEnum.OPEN, JobStatusEnum.CLOSED, JobStatusEnum.CANCELED],
      },
      date: {
        $gte: format(getUtcStartOfDay(new Date()), dbDayDateToken),
      },
      invitees: {
        $elemMatch: {
          userId: Meteor.userId(),
          status: InviteeStatusEnum.OPEN,
        },
      },
    });
  }

  return conditions;
}

